import { useState } from "react";
import { Link, useLocation, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Logo from "./Logo";

// "Lesson Organizer Test Page" is hidden on production — only emulator and staging.
const showTestPage = import.meta.env.VITE_SENTRY_ENVIRONMENT !== 'production';

export default function AdminLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { logout, isRoot, userProfile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const role = userProfile?.role;
  const isAdmin = role === 'admin' || role === 'root';

  const isActive = (path: string) => location.pathname === path;

  // Each nav item declares which roles may see it (undefined = admin/root only).
  // The test page additionally requires a non-production environment.
  const allNavItems = [
    {
      path: "/admin/users",
      label: "Users",
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="9" cy="7" r="4" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      path: "/admin/lessons",
      label: "Lessons",
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20L8 17L12 20L16 17L20 20V4C20 3.46957 19.7893 2.96086 19.4142 2.58579C19.0391 2.21071 18.5304 2 18 2H16" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      path: "/admin/chats",
      label: "Chats",
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      path: "/admin/metrics",
      label: "Metrics",
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 20V10M12 20V4M6 20V14" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      path: "/admin/invitations",
      label: "Invitations",
      roles: ['admin', 'root', 'sales'],
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      path: "/admin/drill-library",
      label: "Drill Library",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      path: "/admin/pro-library",
      label: "Pro Library",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      path: "/admin/support",
      label: "Support",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      path: "/admin/billing",
      label: "Billing",
      roles: ['admin', 'root', 'accounts'],
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="5" width="20" height="14" rx="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 10h20" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      path: "/admin/reconciliation",
      label: "Reconcile",
      roles: ['admin', 'root', 'accounts'],
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 11l3 3L22 4" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    ...(showTestPage ? [{
      path: "/video-draft-test",
      label: "Video",
      roles: ['admin', 'root', 'sales'],
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V16C4 16.5304 4.21071 17.0391 4.58579 17.4142C4.96086 17.7893 5.46957 18 6 18H14C14.5304 18 15.0391 17.7893 15.4142 17.4142C15.7893 17.0391 16 16.5304 16 16V4C16 3.46957 15.7893 2.96086 15.4142 2.58579C15.0391 2.21071 14.5304 2 14 2Z" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M8 6H12M8 10H12M8 14H10" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    }] : []),
    ...(isRoot ? [{
      path: "/admin/action-log",
      label: "Action Log",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 8v4l3 3M12 2a10 10 0 100 20A10 10 0 0012 2z" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    }, {
      path: "/admin/staff",
      label: "Staff",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 15c-4.418 0-8 1.79-8 4v1h16v-1c0-2.21-3.582-4-8-4z" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="12" cy="8" r="4" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M20 8v6M23 11h-6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    }] : []),
  ];

  // Filter to items this role is allowed to see.
  // Items without a `roles` list are admin/root only.
  const navItems = allNavItems.filter(item =>
    'roles' in item ? item.roles!.includes(role ?? '') : isAdmin
  );

  // Sidebar-only labels (longer versions)
  const sidebarLabels: Record<string, string> = {
    "/admin/users": "Users & Coaches",
    "/video-draft-test": "Lesson Organizer Test Page",
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row">
      {/* CSS overrides for mobile bottom nav visibility */}
      <style>{`
        #admin-mobile-bottom-nav {
          display: flex !important;
          position: fixed !important;
          bottom: 0 !important;
          left: 0 !important;
          right: 0 !important;
          z-index: 9999 !important;
          background-color: white !important;
        }
        @media (min-width: 768px) {
          #admin-mobile-bottom-nav {
            display: none !important;
          }
        }
      `}</style>

      {/* Sidebar - Desktop only */}
      <aside className="hidden md:flex md:flex-col w-64 bg-white border-r border-gray-200">
        {/* Logo */}
        <div className="px-6 py-6 border-b border-gray-200">
          <Logo showText={true} href="/admin/users" size="md" textColor="text-tennis-green" />
          <p className="text-xs text-gray-500 mt-2">Admin Panel</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg no-underline transition ${
                isActive(item.path)
                  ? "bg-tennis-green/10 text-tennis-green font-medium"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <span className="text-xl">{item.icon}</span>
              <span>{sidebarLabels[item.path] || item.label}</span>
            </Link>
          ))}
        </nav>

        {/* Footer */}
        <div className="px-4 py-6 border-t border-gray-200 space-y-2">
          <Link
            to="/admin/account"
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-100 no-underline transition"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="10" cy="10" r="3" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.22 4.22l1.42 1.42M14.36 14.36l1.42 1.42M4.22 15.78l1.42-1.42M14.36 5.64l1.42-1.42" strokeLinecap="round"/>
            </svg>
            <span>Account</span>
          </Link>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition bg-transparent border-none cursor-pointer text-left"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M13 3H16C16.5523 3 17 3.44772 17 4V16C17 16.5523 16.5523 17 16 17H13M8 14L3 10M3 10L8 6M3 10H13" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile header - hidden on desktop */}
      <header className="md:hidden bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-50">
        <div className="flex justify-center items-center">
          <div className="flex flex-col items-center">
            <Logo showText={true} href="/admin/users" size="sm" textColor="text-tennis-green" />
            <p className="text-[10px] text-gray-500 mt-0.5">Admin Panel</p>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 overflow-auto pb-20 md:pb-0">
        <Outlet />
      </main>

      {/* Bottom Navigation - Mobile only */}
      <nav id="admin-mobile-bottom-nav">
        <div className="flex justify-around items-center py-2 w-full border-t border-gray-200">
          {/* Menu popup button */}
          <button
            onClick={() => setMenuOpen(true)}
            className="flex flex-col items-center gap-0.5 px-4 py-1 text-gray-500 bg-transparent border-none cursor-pointer"
          >
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h14M3 10h14M3 14h14" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="text-[10px] leading-tight">Menu</span>
          </button>

          {/* Account */}
          <Link
            to="/admin/account"
            className="flex flex-col items-center gap-0.5 px-4 py-1 no-underline text-gray-500 transition"
          >
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="10" cy="10" r="3" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.22 4.22l1.42 1.42M14.36 14.36l1.42 1.42M4.22 15.78l1.42-1.42M14.36 5.64l1.42-1.42" strokeLinecap="round"/>
            </svg>
            <span className="text-[10px] leading-tight">Account</span>
          </Link>

          {/* Logout */}
          <button
            data-testid="admin-logout-btn"
            onClick={() => { logout(); navigate("/login"); }}
            className="flex flex-col items-center gap-0.5 px-4 py-1 text-red-500 bg-transparent border-none cursor-pointer"
          >
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M13 3H16C16.5523 3 17 3.44772 17 4V16C17 16.5523 16.5523 17 16 17H13M8 14L3 10M3 10L8 6M3 10H13" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="text-[10px] leading-tight">Logout</span>
          </button>
        </div>
      </nav>

      {/* Mobile menu popup sheet */}
      {menuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-[10000]"
            onClick={() => setMenuOpen(false)}
          />
          <div className="fixed bottom-[56px] left-0 right-0 bg-white rounded-t-2xl z-[10001] pb-safe overflow-y-auto max-h-[70vh]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <span className="text-sm font-semibold text-gray-700">Admin Menu</span>
              <button
                onClick={() => setMenuOpen(false)}
                className="text-gray-400 bg-transparent border-none cursor-pointer p-1"
              >
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 5L5 15M5 5l10 10" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="px-3 py-3 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl no-underline transition ${
                    isActive(item.path)
                      ? "bg-tennis-green/10 text-tennis-green font-medium"
                      : "text-gray-600"
                  }`}
                >
                  <span className="[&>svg]:w-[18px] [&>svg]:h-[18px]">{item.icon}</span>
                  <span className="text-[14px]">{item.label}</span>
                </Link>
              ))}
              <Link
                to="/discover"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl no-underline text-gray-600 transition"
              >
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 10L10 3L17 10M4 9V17H7V13H13V17H16V9" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="text-[14px]">App</span>
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
